import React, { useState } from 'react'
import { useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { z } from 'zod'
import { useNavigate, useLocation, Link } from 'react-router-dom'
import { Eye, EyeOff, Mail, Lock, Dumbbell } from 'lucide-react'
import { cn } from '@/lib/utils'
import { Button } from '@/components/ui/Button'
import { Input } from '@/components/ui/Input'
import { useAuthStore } from '@/store/authStore'
import { toast } from '@/components/ui/Toast'
import { apiPost } from '@/lib/api'
import type { LoginResponse } from '@/types'

// ─── Validation Schema ────────────────────────────────────────────────────────
const loginSchema = z.object({
  email: z
    .string()
    .min(1, 'E-posta adresi gereklidir.')
    .email('Geçerli bir e-posta adresi girin.'),
  password: z
    .string()
    .min(1, 'Şifre gereklidir.')
    .min(6, 'Şifre en az 6 karakter olmalıdır.'),
})

type LoginFormValues = z.infer<typeof loginSchema>

// ─── LoginPage ────────────────────────────────────────────────────────────────
export const LoginPage: React.FC = () => {
  const navigate  = useNavigate()
  const location  = useLocation()
  const { login } = useAuthStore()

  const [showPassword, setShowPassword] = useState(false)
  const [serverError,  setServerError]  = useState<string | null>(null)

  const {
    register,
    handleSubmit,
    formState: { errors, isSubmitting },
  } = useForm<LoginFormValues>({
    resolver: zodResolver(loginSchema),
  })

  const onSubmit = async (data: LoginFormValues) => {
    setServerError(null)
    try {
      const res = await apiPost<LoginResponse>('/auth/login', data)
      login(res.user, res.accessToken, res.refreshToken)
      toast.success('Hoş geldiniz!', `Merhaba, ${res.user.firstName}`)
      const previousPath = (location.state as { from?: Location })?.from?.pathname
      const roleDefault = res.user.role === 'SuperAdmin' ? '/superadmin' : '/dashboard'
      navigate(previousPath ?? roleDefault, { replace: true })
    } catch (err: unknown) {
      const axiosError = err as { response?: { data?: { errors?: string[]; detail?: string } } }
      const msg =
        axiosError.response?.data?.errors?.[0] ??
        axiosError.response?.data?.detail ??
        'Giriş yapılamadı. Lütfen bilgilerinizi kontrol edin.'
      setServerError(msg)
    }
  }

  return (
    <div className="min-h-dvh flex">
      {/* ── Left: Decorative panel ──────────────────────── */}
      <div className="hidden lg:flex lg:w-1/2 xl:w-3/5 relative overflow-hidden bg-gradient-to-br from-primary-600 via-primary-700 to-primary-900">
        {/* Background patterns */}
        <div className="absolute inset-0 opacity-10">
          <div className="absolute top-0 right-0 w-96 h-96 rounded-full bg-white/20 translate-x-32 -translate-y-32" />
          <div className="absolute bottom-0 left-0 w-80 h-80 rounded-full bg-white/10 -translate-x-20 translate-y-20" />
          <div className="absolute top-1/2 left-1/3 w-64 h-64 rounded-full bg-white/10 -translate-y-1/2" />
        </div>

        <div className="relative z-10 flex flex-col justify-between p-12 text-white w-full">
          {/* Logo */}
          <div className="flex items-center gap-3">
            <div className="h-10 w-10 rounded-2xl bg-white/20 backdrop-blur flex items-center justify-center">
              <Dumbbell className="h-5 w-5 text-white" />
            </div>
            <span className="text-2xl font-bold tracking-tight">SporOkulu</span>
          </div>

          {/* Headline */}
          <div className="space-y-6">
            <div>
              <h1 className="text-5xl font-extrabold leading-tight tracking-tight">
                Spor Okulunuzu<br />
                Dijitalleştirin
              </h1>
              <p className="mt-4 text-lg text-white/75 max-w-md leading-relaxed">
                Öğrenci yönetiminden yoklamaya, ödemelerden raporlamaya —
                tüm süreçlerinizi tek platformdan kolayca yönetin.
              </p>
            </div>

            {/* Feature pills */}
            <div className="flex flex-wrap gap-2">
              {['Öğrenci Yönetimi', 'QR Yoklama', 'Otomatik Ödeme', 'SMS/Email', 'Raporlama'].map((f) => (
                <span
                  key={f}
                  className="px-3 py-1 rounded-full bg-white/15 backdrop-blur text-sm font-medium"
                >
                  {f}
                </span>
              ))}
            </div>
          </div>

          {/* Stats */}
          <div className="grid grid-cols-3 gap-6">
            {[
              { value: '500+', label: 'Spor Okulu' },
              { value: '50K+', label: 'Öğrenci' },
              { value: '99.9%', label: 'Çalışma Süresi' },
            ].map((stat) => (
              <div key={stat.label}>
                <p className="text-3xl font-bold tabular-nums">{stat.value}</p>
                <p className="text-sm text-white/60 mt-0.5">{stat.label}</p>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* ── Right: Login form ───────────────────────────── */}
      <div className="flex-1 flex flex-col items-center justify-center p-6 sm:p-10 bg-white dark:bg-slate-900">
        <div className="w-full max-w-sm">
          {/* Mobile logo */}
          <div className="flex items-center gap-2 mb-8 lg:hidden">
            <div className="h-8 w-8 rounded-xl bg-primary-600 flex items-center justify-center">
              <Dumbbell className="h-4 w-4 text-white" />
            </div>
            <span className="text-xl font-bold text-slate-900 dark:text-slate-100">SporOkulu</span>
          </div>

          {/* Heading */}
          <div className="mb-8">
            <h2 className="text-2xl font-bold text-slate-900 dark:text-slate-100 tracking-tight">
              Tekrar Hoş Geldiniz
            </h2>
            <p className="mt-1.5 text-sm text-slate-500 dark:text-slate-400">
              Hesabınıza giriş yapın
            </p>
          </div>

          {/* Server error alert */}
          {serverError && (
            <div
              role="alert"
              className="mb-5 flex items-start gap-3 rounded-xl border border-red-200 bg-red-50 dark:border-red-800 dark:bg-red-900/20 px-4 py-3"
            >
              <span className="text-sport-red text-sm">{serverError}</span>
            </div>
          )}

          {/* Form */}
          <form
            onSubmit={handleSubmit(onSubmit)}
            noValidate
            aria-label="Giriş formu"
            className="space-y-4"
          >
            <Input
              label="E-posta Adresi"
              type="email"
              autoComplete="email"
              placeholder="ornek@sporokulu.com"
              required
              leftIcon={<Mail className="h-4 w-4" />}
              error={errors.email?.message}
              {...register('email')}
            />

            <div>
              <Input
                label="Şifre"
                type={showPassword ? 'text' : 'password'}
                autoComplete="current-password"
                placeholder="••••••••"
                required
                leftIcon={<Lock className="h-4 w-4" />}
                rightIcon={
                  <button
                    type="button"
                    onClick={() => setShowPassword((v) => !v)}
                    className="text-slate-400 hover:text-slate-600 dark:hover:text-slate-300 transition-colors"
                    aria-label={showPassword ? 'Şifreyi gizle' : 'Şifreyi göster'}
                  >
                    {showPassword
                      ? <EyeOff className="h-4 w-4" />
                      : <Eye className="h-4 w-4" />
                    }
                  </button>
                }
                error={errors.password?.message}
                {...register('password')}
              />
              <div className="flex justify-end mt-1.5">
                <Link
                  to="/sifremi-unuttum"
                  className="text-xs text-primary-600 dark:text-primary-400 hover:underline focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-primary-500 rounded"
                >
                  Şifremi Unuttum
                </Link>
              </div>
            </div>

            <Button
              type="submit"
              fullWidth
              size="lg"
              loading={isSubmitting}
              className="mt-2"
            >
              {isSubmitting ? 'Giriş yapılıyor…' : 'Giriş Yap'}
            </Button>
          </form>

          {/* Footer */}
          <p className="mt-8 text-center text-xs text-slate-400 dark:text-slate-500">
            SporOkulu SaaS &copy; {new Date().getFullYear()}&ensp;·&ensp;
            <Link to="/gizlilik" className="hover:text-slate-600 hover:underline">Gizlilik</Link>
            &ensp;·&ensp;
            <Link to="/kvkk" className="hover:text-slate-600 hover:underline">KVKK</Link>
          </p>
        </div>
      </div>
    </div>
  )
}
